import produce from "immer";
import create from "zustand";
import { ArtistId, PlaylistOrder } from "../../models/Artist";
import { CURRENT_PRIVACY_POLICY_VERSION, OwnerId, PrivacyConsent } from "../../models/User";
import { ArtistService } from "../../services/ArtistService";
import { LoginService } from "../../services/LoginService";
import { ProfileService } from "../../services/ProfileService";
import { SettingsService } from "../../services/SettingsService";
import { useTransmission } from "./useTransmission";

export type ProfileState = {
  darkMode: boolean;
compact:boolean;
  status: "unknown" | "authenticating" | "authenticated" | "unauthenticated";
  authenticated: boolean;
  ownerId: OwnerId;
  authorId?: ArtistId;
  menuEnabled: boolean;
  /** The signed-in artist's own privacy posture, read from their artist doc at
   *  login and kept current as they toggle it in Account settings. It is the
   *  default tier for songs they transmit (see SongService.transmit's
   *  precedence chain), so the transmit form reads it to show the tier a new
   *  song will actually get. Undefined until the artist doc has loaded. */
  privacy?: "public" | "member";
  /** True once we've checked the artist doc and confirmed there's no
   *  current-version consent record — meaning we need to prompt. */
  needsConsent: boolean;
  observeAuthentication: (
    onAuthenticated: (artistId: ArtistId, ownerId: string) => void,
    onUnAuthed: (artistId: ArtistId) => void
  ) => void;
  setDarkMode: (isOn: boolean) => void;
  setCompactMode: (isOn: boolean) => void;
  /** Persist one profile field. Rejects if the write is refused, so callers can tell the user. */
  changeProfile: (item: string, value: string) => Promise<void>;
  changeUserPlaylistOrder: (item: string, value: PlaylistOrder[]) => void;
  /** Persist the user's first-login privacy choice + agreement. Updates
   *  both the consent record and the privacy field in one write. */
  recordConsent: (privacyChoice: "public" | "member") => Promise<void>;
};

export const useProfile = create<ProfileState>((set, get) => ({
  menuEnabled: true,
  status: "unknown",
  darkMode: false,
  compact: false,
  authenticated: false,
  ownerId: "",
  needsConsent: false,
  recordConsent: async (privacyChoice) => {
    const { authorId, ownerId } = get();
    if (!authorId) return;
    const consent: PrivacyConsent = {
      policyVersion: CURRENT_PRIVACY_POLICY_VERSION,
      privacyChoice,
      agreedToTerms: true,
      consentedAt: Date.now(),
    };
    await ProfileService.changeProfile(
      { ownerId, consent, privacy: privacyChoice },
      authorId,
    );
    set({ needsConsent: false, privacy: privacyChoice });
  },
  changeProfile: (item, value: string) => {
    const { authorId, ownerId } = get();
    let change = { ownerId } as any;
    change[item] = value;
    // Mirror a privacy change locally so the transmit form's default tier
    // follows the setting without a reload.
    if (item === "privacy" && (value === "public" || value === "member")) {
      set({ privacy: value });
    }
    if (!authorId) return Promise.reject(new Error("No profile is loaded yet."));
    return ProfileService.changeProfile(change, authorId).then(() => undefined);
  },
  changeUserPlaylistOrder: (fieldName: string, value: PlaylistOrder[]) => {
    const { authorId, ownerId } = get();
    let change = { ownerId } as any;
    change.playlistsOrder = { [fieldName]: value };

    authorId && ProfileService.changeProfile(change, authorId);
  },
  setDarkMode: (isOn) => {
    const { ownerId } = get();
    set({ darkMode: isOn });
    const darkmodeSetting = { theme: isOn ? "dark" : "light" };
    ownerId && SettingsService.changeSetting(darkmodeSetting, ownerId);
  },
  setCompactMode: (isOn) => {
    const { ownerId } = get();
    set({ compact: isOn });
    const compactModeSetting = { compact: isOn ? "compact" : "default" };
    ownerId && SettingsService.changeSetting(compactModeSetting, ownerId);
  },

  observeAuthentication: (onAuthenticated, onUnAuthed) => {
    const authenticate = async (authorId: ArtistId, ownerId: string) => {
      const profile = get();
      const authenticatedProfile = produce(profile, (draftState) => {
        draftState.authenticated = true;
        draftState.authorId = authorId;
        draftState.ownerId = ownerId;
        draftState.status = "authenticated";
      });
      set(authenticatedProfile);
      onAuthenticated(authorId, ownerId);
      const settings = await SettingsService.ArtistSettings(ownerId);
      if (settings.theme === "dark") {
        set({ darkMode: true });
      } else {
        set({ darkMode: false });
      }
      if (settings.compact === "compact") {
        set({ compact: true });
      } else {
        set({ compact: false });
      }

      // FERPA / privacy consent check: if this user has no consent record
      // (or it's for an older policy version), flag for the first-login
      // privacy modal to render. Failure to read the artist doc is treated
      // as "needs consent" — fail safe toward asking.
      try {
        const artist = await ArtistService.fetch(authorId);
        const consent = artist && (artist as any).consent;
        const upToDate =
          consent && consent.policyVersion === CURRENT_PRIVACY_POLICY_VERSION;
        // A profile with no (or an unrecognised) `privacy` field has made no
        // deliberate choice: the rules read it as signed-in-only (S06), and
        // songs fall through to the instance default rather than being
        // published because of a historical absent flag.
        const artistPrivacy = artist && (artist as any).privacy;
        set({
          needsConsent: !upToDate,
          privacy:
            artistPrivacy === "member" || artistPrivacy === "public"
              ? artistPrivacy
              : undefined,
        });
      } catch {
        set({ needsConsent: true });
      }
    };

    const logOut = () => {
      const profile = get();
      const id = get().authorId || "";
      const loggedOutProfile = produce(profile, (draftState) => {
        draftState.authenticated = false;
        draftState.authorId = "";
        draftState.ownerId = "";
        draftState.status = "unauthenticated";
        draftState.needsConsent = false;
        draftState.privacy = undefined;
      });
      set(loggedOutProfile);
      // useTransmission is a single global store, not scoped per signed-in
      // user — on a shared/classroom device, a forgotten logout could
      // otherwise carry one student's in-progress draft (or "today's
      // transmission") straight into the next student's session.
      useTransmission.getState().resetForLogout();
      onUnAuthed(id);
    };

    LoginService.watchAuthState(authenticate, logOut);
  },
}));
